/**
 * OWNER-FUNNEL-FOLDIN-V1 (Story 6.2) -- frontend view types + pure
 * helpers for the owner funnel snapshot.
 *
 * TYPES ONLY mirror of the backend wire contract
 * (lib/owner-funnel-contract.ts) -- the two TS packages are separate, so
 * shapes are mirrored locally (the CRM-foundation convention). ALL COPY
 * (labels, tooltips, honest-state strings) rides the WIRE and is rendered
 * VERBATIM (A2): this module deliberately contains no metric copy of its
 * own -- only structural formatting (percent/count rendering) and the
 * filter -> query-param mapping.
 */

// ---- Wire mirror (lib/owner-funnel-contract.ts) ----

export type OwnerFunnelMetricStatus =
  | 'ready'
  | 'not_connected'
  | 'no_data'
  | 'partial';

export interface OwnerFunnelDrilldown {
  readonly path: string;
  readonly params?: Readonly<Record<string, string>>;
}

export interface OwnerFunnelMetric {
  readonly value: number | null;
  readonly label: string;
  readonly tooltip: string;
  readonly status: OwnerFunnelMetricStatus;
  readonly sourceFields: readonly string[];
  readonly previousValue?: number | null;
  readonly drilldown?: OwnerFunnelDrilldown;
}

export type OwnerFunnelRangePreset = '7d' | '30d' | '3m' | '6m' | 'custom';

export interface OwnerFunnelRange {
  readonly preset: OwnerFunnelRangePreset;
  readonly from: string;
  readonly to: string;
  readonly timezoneNote: string;
}

export interface OwnerFunnelKpis {
  readonly totalLeads: OwnerFunnelMetric;
  readonly contacted: OwnerFunnelMetric;
  readonly appointments: OwnerFunnelMetric;
  readonly convertedToPatient: OwnerFunnelMetric;
  readonly conversionRate: OwnerFunnelMetric;
  readonly staleUnworked: OwnerFunnelMetric;
}

export interface OwnerFunnelStage {
  readonly key: 'leads' | 'contacted' | 'appointment' | 'converted';
  readonly metric: OwnerFunnelMetric;
}

export interface OwnerFunnelTrendBucket {
  readonly start: string;
  readonly leads: number;
  readonly conversions: number;
}

export interface OwnerFunnelTrends {
  readonly bucketUnit: 'day' | 'week';
  readonly buckets: readonly OwnerFunnelTrendBucket[];
  readonly leadVolume: OwnerFunnelMetric;
  readonly conversions: OwnerFunnelMetric;
  readonly comparison: {
    readonly threeMonthAvgLeadsPerMonth: OwnerFunnelMetric;
    readonly sixMonthAvgLeadsPerMonth: OwnerFunnelMetric;
    readonly threeMonthAvgConversionsPerMonth: OwnerFunnelMetric;
    readonly sixMonthAvgConversionsPerMonth: OwnerFunnelMetric;
  };
}

export interface OwnerFunnelSourceRow {
  readonly sourceNormalized: string;
  readonly sourceLabel: string;
  readonly campaignLabel: string | null;
  readonly campaignStateLabel: string | null;
  readonly leads: OwnerFunnelMetric;
  readonly contactedPct: OwnerFunnelMetric;
  readonly appointmentPct: OwnerFunnelMetric;
  readonly conversionPct: OwnerFunnelMetric;
  readonly spendStatus: OwnerFunnelMetric;
  readonly revenueAttribution: OwnerFunnelMetric;
  readonly dark: boolean;
  readonly drilldown?: OwnerFunnelDrilldown;
}

export interface OwnerFunnelClinicRow {
  readonly clinicId: number | null;
  readonly clinicLabel: string;
  readonly leads: OwnerFunnelMetric;
  readonly contacted: OwnerFunnelMetric;
  readonly appointments: OwnerFunnelMetric;
  readonly converted: OwnerFunnelMetric;
  readonly staleUnworked: OwnerFunnelMetric;
  readonly drilldown?: OwnerFunnelDrilldown;
}

export interface OwnerFunnelLeakageFact {
  readonly key: string;
  readonly text: string;
  readonly severity: 'high' | 'medium';
}

export interface OwnerFunnelLeakage {
  readonly facts: readonly OwnerFunnelLeakageFact[];
  readonly spend: OwnerFunnelMetric;
  readonly revenueAttribution: OwnerFunnelMetric;
}

export interface OwnerFunnelStateCopy {
  readonly revenueNotConnected: string;
  readonly spendNotConnected: string;
  readonly spendStale: string;
  readonly spendNoData: string;
  readonly campaignNotTagged: string;
  readonly campaignPartial: string;
  readonly channelNotLive: string;
  readonly noData: string;
  readonly partialScanBanner: string;
  readonly timezoneNote: string;
  readonly noLeakageFacts: string;
  readonly pendingClinicRow: string;
  /** MONEY-COMPLETION-V1 (R-47/R-48); optional -- older backends omit. */
  readonly attributionWindowRule?: string;
  readonly conversionGrainNote?: string;
}

/** MONEY-COMPLETION-V1: the window's recorded-collections block (concepts
 *  1 + 2 for the SELECTED period; total = attributed + unattributed by
 *  construction -- the reconciliation strip renders the identity). */
export interface OwnerFunnelRecordedCollections {
  readonly total: OwnerFunnelMetric;
  readonly attributedInr: number;
  readonly notLinkedInr: number;
  readonly transactionCount: number;
  readonly distinctPaidVisits: number;
  readonly averagePerPaidVisit: OwnerFunnelMetric;
}

export interface OwnerFunnelSnapshot {
  readonly range: OwnerFunnelRange;
  readonly filters: {
    readonly clinicId: number | null;
    readonly source: string | null;
  };
  readonly kpis: OwnerFunnelKpis;
  readonly funnel: readonly OwnerFunnelStage[];
  readonly trends: OwnerFunnelTrends;
  readonly sourceTable: readonly OwnerFunnelSourceRow[];
  /** Rung 3 honesty row (PATIENT-PAYMENTS-REVENUE-SPINE-V1): collected
   *  revenue whose patient has no linked lead. OPTIONAL on the wire so
   *  older fixtures stay valid; absent/null = the row does not render. */
  readonly revenueNotLinked?: OwnerFunnelMetric | null;
  /** MONEY-COMPLETION-V1: optional so older fixtures stay valid; null =
   *  the payments spine is not connected (the rung 1 posture). */
  readonly recordedCollections?: OwnerFunnelRecordedCollections | null;
  readonly clinicTable: readonly OwnerFunnelClinicRow[];
  readonly leakage: OwnerFunnelLeakage;
  readonly scanComplete: boolean;
  readonly scanLimit: number;
  readonly staleMinutes: number;
  readonly darkSources: readonly string[];
  readonly clinics: readonly {
    readonly clinicId: number;
    readonly clinicLabel: string | null;
  }[];
  readonly sourceOptions: readonly {
    readonly value: string;
    readonly label: string;
  }[];
  readonly stateCopy: OwnerFunnelStateCopy;
}

// ---- Filter state -> query params ----

export interface OwnerFunnelFilterState {
  readonly range: OwnerFunnelRangePreset;
  /** YYYY-MM-DD, used only when range === 'custom'. */
  readonly from: string;
  readonly to: string;
  /** 'all' or a clinic id string. */
  readonly clinicId: string;
  /** 'all' or a source_normalized token. */
  readonly source: string;
}

export const OWNER_FUNNEL_DEFAULT_FILTERS: OwnerFunnelFilterState =
  Object.freeze({
    range: '30d',
    from: '',
    to: '',
    clinicId: 'all',
    source: 'all',
  });

/** Query params for fetchOwnerFunnelSnapshot ('all'/defaults omitted). */
export function ownerFunnelQueryParams(
  f: OwnerFunnelFilterState,
): Record<string, string> {
  const params: Record<string, string> = { range: f.range };
  if (f.range === 'custom') {
    if (f.from) params.from = f.from;
    if (f.to) params.to = f.to;
  }
  if (f.clinicId !== 'all' && f.clinicId !== '') params.clinicId = f.clinicId;
  if (f.source !== 'all' && f.source !== '') params.source = f.source;
  return params;
}

// ---- Structural formatting (numbers only -- copy rides the wire) ----

/** '--' is the honest no-value rendering (never a fake 0 / 0%). */
export const NO_VALUE = '--';

/** Render a 0..1 fraction as a whole percent, or the honest no-value. */
export function formatPct(value: number | null): string {
  if (value === null) return NO_VALUE;
  return `${Math.round(value * 100)}%`;
}

/** Render a count, or the honest no-value. */
export function formatCount(value: number | null): string {
  if (value === null) return NO_VALUE;
  return value.toLocaleString('en-IN');
}

/** Render INR spend (ASCII-safe, no currency glyph). */
export function formatInr(value: number | null): string {
  if (value === null) return NO_VALUE;
  return `INR ${value.toLocaleString('en-IN')}`;
}

/** Period-over-period delta line, only when both values exist. */
export function formatDelta(metric: OwnerFunnelMetric): string | null {
  if (
    metric.previousValue === undefined ||
    metric.previousValue === null ||
    metric.value === null
  ) {
    return null;
  }
  const diff = metric.value - metric.previousValue;
  const sign = diff > 0 ? '+' : '';
  return `${sign}${diff.toLocaleString('en-IN')} vs previous period (${metric.previousValue.toLocaleString('en-IN')})`;
}

/** Build the drilldown href from the wire-declared target (A3). */
export function drilldownHref(d: OwnerFunnelDrilldown): string {
  const entries = Object.entries(d.params ?? {});
  if (entries.length === 0) return d.path;
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${d.path}?${qs}`;
}

/**
 * Numeric cell text (counts / percents): a real value renders its
 * number; null renders the honest no-value dash (the row/tile state
 * label carries the wire copy explaining why).
 */
export function metricCellText(
  metric: OwnerFunnelMetric,
  kind: 'count' | 'pct',
): string {
  if (kind === 'pct') return formatPct(metric.value);
  return formatCount(metric.value);
}

/** Spend cell/tile text -- the A4 freshness states, wire copy verbatim. */
export function spendCellText(
  metric: OwnerFunnelMetric,
  stateCopy: OwnerFunnelStateCopy,
): string {
  if (metric.status === 'not_connected') return stateCopy.spendNotConnected;
  if (metric.status === 'no_data') return stateCopy.spendNoData;
  if (metric.status === 'partial') {
    return `${formatInr(metric.value)} (${stateCopy.spendStale})`;
  }
  return formatInr(metric.value);
}

/**
 * Revenue-attribution cell text (PATIENT-PAYMENTS-REVENUE-SPINE-V1, rung 3).
 * Mirrors spendCellText's status ladder: not_connected renders the rung 1
 * copy verbatim (the pre-spine posture, byte-identical); no_data = spine
 * live but no payments traced to this row in the window; partial = the
 * bounded payment scan capped (a labeled lower bound, 'at least').
 */
export function revenueCellText(
  metric: OwnerFunnelMetric,
  stateCopy: OwnerFunnelStateCopy,
): string {
  if (metric.status === 'not_connected') return stateCopy.revenueNotConnected;
  if (metric.status === 'no_data') return stateCopy.noData;
  if (metric.status === 'partial') {
    return `${formatInr(metric.value)} (at least)`;
  }
  return formatInr(metric.value);
}
